import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Breadcrumbs,
    Button,
    Card,
    Chip,
    CircularProgress,
    Divider,
    FormLabel,
    IconButton,
    Input,
    Link,
    Modal,
    ModalClose,
    ModalDialog,
    Option,
    Select,
    Sheet,
    Stack,
    Tab,
    Table,
    TabList,
    TabPanel,
    Tabs,
    Typography,
} from '@mui/joy';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import GroupRoundedIcon from '@mui/icons-material/GroupRounded';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import MarkEmailReadRoundedIcon from '@mui/icons-material/MarkEmailReadRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import GeneralApiService from '../../axios/Axios';
import ResponsiveTableWrapper, {
    FilterBarLeft,
    FilterBarRight,
    ResponsiveFilterBar,
} from '../ui/ResponsiveTableWrapper';

type FranchiseLead = {
    id: number;
    name: string;
    phone: string;
    email: string;
    franchise_type_display: string;
    territory_location: string;
    source: string;
    ip_address: string;
    created_at: string;
    email_unsubscribed: boolean;
    email_suppressed: boolean;
    email_bounced: boolean;
    email_complained: boolean;
    email_suppression_reason: string;
    latest_enrollment?: {
        chain_name: string;
        current_stage: number;
        total_emails: number;
        is_active: boolean;
        paused: boolean;
        last_email_sent_at?: string;
        next_email_due_at?: string;
    } | null;
};

type EmailChain = {
    slug: string;
    name: string;
    description: string;
    message_stream: string;
    total_emails: number;
    emails: Array<{
        stage: number;
        subject: string;
        send_after_days: number;
        template: string;
    }>;
    counts: {
        total_enrolled: number;
        active: number;
        completed: number;
        paused: number;
        unsubscribed_or_suppressed: number;
        sent: number;
        failed: number;
        next_due: number;
    };
};

type EmailEvent = {
    id: number;
    event_type: string;
    event_type_display: string;
    chain_slug: string;
    email_stage?: number;
    subject: string;
    details?: Record<string, unknown>;
    created_at: string;
    lead: {
        id: number;
        name: string;
        email: string;
    };
};

type ChainsResponse = {
    summary: {
        total_franchise_leads: number;
        active_enrollments: number;
        emails_sent_today: number;
        daily_limit: number;
        remaining_today: number;
        daily_limit_sources: string[];
        suppressed_or_unsubscribed: number;
        failed_sends: number;
    };
    chains: EmailChain[];
};

type PaginatedFranchiseLeadsResponse = {
    results: FranchiseLead[];
    count: number;
    page: number;
    page_size: number;
    total_pages: number;
};

function formatDate(value?: string | null) {
    if (!value) return '-';
    return new Date(value).toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

function statusChip(lead: FranchiseLead) {
    if (lead.email_complained) return <Chip color="danger" size="sm">Complained</Chip>;
    if (lead.email_bounced) return <Chip color="danger" size="sm">Bounced</Chip>;
    if (lead.email_suppressed) return <Chip color="warning" size="sm">Suppressed</Chip>;
    if (lead.email_unsubscribed) return <Chip color="neutral" size="sm">Unsubscribed</Chip>;
    if (lead.latest_enrollment?.is_active) return <Chip color="success" size="sm">Active Chain</Chip>;
    return <Chip color="primary" size="sm">Lead</Chip>;
}

function getLeadStatusValue(lead: FranchiseLead) {
    if (lead.email_complained) return 'complained';
    if (lead.email_bounced) return 'bounced';
    if (lead.email_suppressed) return 'suppressed';
    if (lead.email_unsubscribed) return 'unsubscribed';
    if (lead.latest_enrollment?.is_active) return 'active';
    return 'lead';
}

function labelDisplayedRows({ from, to, count }: { from: number; to: number; count: number }) {
    return `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`;
}

function eventDetailsText(event: EmailEvent) {
    if (!event.details) return '-';
    const error = event.details.error;
    const reason = event.details.reason;
    if (typeof error === 'string' && error) return error;
    if (typeof reason === 'string' && reason) return reason;
    return Object.keys(event.details).length ? JSON.stringify(event.details) : '-';
}

function SummaryCard({ icon, label, value, colour = 'primary' }: {
    icon: React.ReactNode;
    label: string;
    value: React.ReactNode;
    colour?: 'primary' | 'success' | 'warning' | 'danger' | 'neutral';
}) {
    return (
        <Card variant="outlined" sx={{ minHeight: 110 }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
                <Sheet
                    variant="soft"
                    color={colour}
                    sx={{ width: 40, height: 40, borderRadius: '50%', display: 'grid', placeItems: 'center' }}
                >
                    {icon}
                </Sheet>
                <Box>
                    <Typography level="body-sm" color="neutral">{label}</Typography>
                    <Typography level="h3">{value}</Typography>
                </Box>
            </Stack>
        </Card>
    );
}

export default function PotentialZees() {
    const [activeTab, setActiveTab] = useState(0);
    const [leads, setLeads] = useState<FranchiseLead[]>([]);
    const [leadsTotal, setLeadsTotal] = useState(0);
    const [chainsData, setChainsData] = useState<ChainsResponse | null>(null);
    const [events, setEvents] = useState<EmailEvent[]>([]);
    const [loading, setLoading] = useState(true);
    const [leadLoading, setLeadLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState<string | null>(null);
    const [error, setError] = useState('');
    const [leadSearch, setLeadSearch] = useState('');
    const [leadStatusFilter, setLeadStatusFilter] = useState('all');
    const [leadPage, setLeadPage] = useState(0);
    const [eventPage, setEventPage] = useState(0);
    const [testEmailChain, setTestEmailChain] = useState<EmailChain | null>(null);
    const [testEmailAddress, setTestEmailAddress] = useState('oliver@yeticlean.co.uk');
    const [testEmailStage, setTestEmailStage] = useState(1);
    const [testEmailMessage, setTestEmailMessage] = useState('');
    const leadRowsPerPage = 25;
    const eventRowsPerPage = 25;

    const loadLeads = async () => {
        setLeadLoading(true);
        setError('');
        try {
            const leadData = await GeneralApiService.getFranchiseProspectLeads({
                page: leadPage + 1,
                page_size: leadRowsPerPage,
                search: leadSearch.trim(),
                status: leadStatusFilter,
            }) as PaginatedFranchiseLeadsResponse;
            setLeads(leadData.results);
            setLeadsTotal(leadData.count);
        } catch (err) {
            console.error(err);
            setError('Unable to load franchise leads.');
        } finally {
            setLeadLoading(false);
        }
    };

    const loadData = async () => {
        setLoading(true);
        setError('');
        try {
            const [leadData, chainData, eventData] = await Promise.all([
                GeneralApiService.getFranchiseProspectLeads({
                    page: leadPage + 1,
                    page_size: leadRowsPerPage,
                    search: leadSearch.trim(),
                    status: leadStatusFilter,
                }) as Promise<PaginatedFranchiseLeadsResponse>,
                GeneralApiService.getFranchiseProspectEmailChains(),
                GeneralApiService.getFranchiseProspectEmailEvents(),
            ]);
            setLeads(leadData.results);
            setLeadsTotal(leadData.count);
            setChainsData(chainData);
            setEvents(eventData);
        } catch (err) {
            console.error(err);
            setError('Unable to load Potential Zee data.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        if (!loading) {
            loadLeads();
        }
    }, [leadPage, leadSearch, leadStatusFilter]);

    const addAllToChain = async (chain: EmailChain) => {
        const confirmed = window.confirm(`Add all eligible franchise leads to "${chain.name}"?`);
        if (!confirmed) return;
        setActionLoading(chain.slug);
        setError('');
        try {
            await GeneralApiService.addAllFranchiseProspectsToEmailChain(chain.slug);
            await loadData();
        } catch (err) {
            console.error(err);
            setError('Unable to add franchise leads to this email chain.');
        } finally {
            setActionLoading(null);
        }
    };

    const openTestEmailModal = (chain: EmailChain) => {
        setTestEmailChain(chain);
        setTestEmailAddress('oliver@yeticlean.co.uk');
        setTestEmailStage(chain.emails[0]?.stage || 1);
        setTestEmailMessage('');
    };

    const sendTestEmail = async () => {
        if (!testEmailChain) return;
        setActionLoading(`test-${testEmailChain.slug}`);
        setError('');
        setTestEmailMessage('');
        try {
            await GeneralApiService.sendFranchiseProspectTestEmail(testEmailChain.slug, {
                email: testEmailAddress,
                stage: testEmailStage,
            });
            setTestEmailMessage(`Test email sent to ${testEmailAddress}.`);
        } catch (err) {
            console.error(err);
            setTestEmailMessage('Unable to send test email. Check the Postmark stream and email address.');
        } finally {
            setActionLoading(null);
        }
    };

    const summary = chainsData?.summary;
    const leadPageCount = leadsTotal;
    const leadFrom = leadPageCount === 0 ? 0 : leadPage * leadRowsPerPage + 1;
    const leadTo = Math.min((leadPage + 1) * leadRowsPerPage, leadPageCount);
    const paginatedEvents = useMemo(
        () => events.slice(eventPage * eventRowsPerPage, eventPage * eventRowsPerPage + eventRowsPerPage),
        [events, eventPage],
    );
    const eventPageCount = events.length;
    const eventFrom = eventPageCount === 0 ? 0 : eventPage * eventRowsPerPage + 1;
    const eventTo = Math.min((eventPage + 1) * eventRowsPerPage, eventPageCount);

    return (
        <>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Breadcrumbs
                    size="sm"
                    aria-label="breadcrumbs"
                    separator={<ChevronRightRoundedIcon />}
                    sx={{ pl: 0 }}
                >
                    <Link underline="none" color="neutral" href="/dashboard" aria-label="Home">
                        <HomeRoundedIcon />
                    </Link>
                    <Link underline="hover" color="neutral" href="/dashboard" fontSize={12} fontWeight={500}>
                        Dashboard
                    </Link>
                    <Typography color="primary" fontWeight={500} fontSize={12}>
                        Potential Zee&apos;s
                    </Typography>
                </Breadcrumbs>
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    mb: 2,
                    gap: 1,
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'start', sm: 'center' },
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                }}
            >
                <Box>
                    <Typography level="h2" component="h1">Potential Zee&apos;s</Typography>
                    <Typography level="body-sm" color="neutral">
                        Franchise prospects, email chain enrolments, and nurture email performance.
                    </Typography>
                </Box>
                <Button variant="outlined" onClick={loadData} loading={loading || leadLoading}>Refresh</Button>
            </Box>

            {error && <Alert color="danger" sx={{ mb: 2 }}>{error}</Alert>}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress size="lg" />
                </Box>
            ) : (
                <>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
                            gap: 2,
                            mb: 2,
                        }}
                    >
                        <SummaryCard icon={<GroupRoundedIcon />} label="Franchise Leads" value={summary?.total_franchise_leads ?? leadsTotal} />
                        <SummaryCard icon={<EmailRoundedIcon />} label="Active Chains" value={summary?.active_enrollments ?? 0} colour="success" />
                        <SummaryCard icon={<MarkEmailReadRoundedIcon />} label="Old Import Sent Today" value={`${summary?.emails_sent_today ?? 0}/${summary?.daily_limit ?? 100}`} />
                        <SummaryCard icon={<WarningRoundedIcon />} label="Suppressed / Unsubscribed" value={summary?.suppressed_or_unsubscribed ?? 0} colour="warning" />
                    </Box>

                    <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value as number)}>
                        <TabList>
                            <Tab>Franchise Leads</Tab>
                            <Tab>Email Chains</Tab>
                            <Tab>Activity</Tab>
                        </TabList>

                        <TabPanel value={0} sx={{ px: 0 }}>
                            <ResponsiveFilterBar>
                                <FilterBarLeft>
                                    <Input
                                        sx={{ width: { xs: '100%', sm: '250px' } }}
                                        startDecorator={<SearchRoundedIcon />}
                                        placeholder="Search by any field..."
                                        value={leadSearch}
                                        onChange={(event) => {
                                            setLeadSearch(event.target.value);
                                            setLeadPage(0);
                                        }}
                                    />
                                    <Select
                                        placeholder="Filter by..."
                                        value={leadStatusFilter}
                                        onChange={(_, value) => {
                                            setLeadStatusFilter(value || 'all');
                                            setLeadPage(0);
                                        }}
                                        startDecorator={<TuneRoundedIcon />}
                                        renderValue={(selected) => (
                                            <Box sx={{ display: 'flex', gap: '0.25rem' }}>
                                                <Chip variant="soft" color="primary">{selected?.label}</Chip>
                                            </Box>
                                        )}
                                        sx={{ minWidth: { xs: '100%', sm: '15rem' } }}
                                    >
                                        <Option value="all">All Franchise Leads</Option>
                                        <Option value="lead">No Active Chain</Option>
                                        <Option value="active">Active Chain</Option>
                                        <Option value="unsubscribed">Unsubscribed</Option>
                                        <Option value="suppressed">Suppressed</Option>
                                        <Option value="bounced">Bounced</Option>
                                        <Option value="complained">Complained</Option>
                                    </Select>
                                </FilterBarLeft>
                                <FilterBarRight>
                                    <Button variant="outlined" onClick={loadData} loading={loading || leadLoading}>Refresh</Button>
                                </FilterBarRight>
                            </ResponsiveFilterBar>

                            <ResponsiveTableWrapper minWidth={1100}>
                                <Table aria-label="franchise leads table" sx={{ mt: 2 }}>
                                    <thead>
                                    <tr>
                                        <th style={{ width: '210px' }}>Date Created</th>
                                        <th>Lead Name</th>
                                        <th>Contact</th>
                                        <th>Franchise Type</th>
                                        <th>Territory</th>
                                        <th>Chain Status</th>
                                        <th style={{ width: '170px' }}>Last Sent</th>
                                        <th style={{ width: '170px' }}>Next Due</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {leads.map((lead) => (
                                        <tr key={lead.id}>
                                            <td>{formatDate(lead.created_at)}</td>
                                            <td>
                                                <Typography level="title-sm">{lead.name}</Typography>
                                                <Typography level="body-xs" color="neutral">{lead.source}</Typography>
                                            </td>
                                            <td>
                                                <Typography level="body-sm">{lead.email}</Typography>
                                                <Typography level="body-xs" color="neutral">{lead.phone}</Typography>
                                            </td>
                                            <td>{lead.franchise_type_display}</td>
                                            <td>
                                                <Typography level="body-sm">{lead.territory_location || '-'}</Typography>
                                                <Typography level="body-xs" color="neutral">{lead.ip_address || '-'}</Typography>
                                            </td>
                                            <td>
                                                <Stack spacing={0.75}>
                                                    {statusChip(lead)}
                                                    {lead.latest_enrollment && (
                                                        <Typography level="body-xs" color="neutral">
                                                            {lead.latest_enrollment.chain_name} ({lead.latest_enrollment.current_stage}/{lead.latest_enrollment.total_emails})
                                                        </Typography>
                                                    )}
                                                    {lead.email_suppression_reason && (
                                                        <Typography level="body-xs" color="danger">{lead.email_suppression_reason}</Typography>
                                                    )}
                                                </Stack>
                                            </td>
                                            <td>{formatDate(lead.latest_enrollment?.last_email_sent_at)}</td>
                                            <td>{formatDate(lead.latest_enrollment?.next_email_due_at)}</td>
                                        </tr>
                                    ))}
                                    {!leads.length && (
                                        <tr>
                                            <td colSpan={8} style={{ textAlign: 'center', padding: '2rem' }}>
                                                <Typography color="neutral">No franchise leads found.</Typography>
                                            </td>
                                        </tr>
                                    )}
                                    </tbody>
                                    <tfoot>
                                    <tr>
                                        <td colSpan={8}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'flex-end' }}>
                                                <Typography textAlign="center" sx={{ minWidth: 90 }}>
                                                    {labelDisplayedRows({ from: leadFrom, to: leadTo, count: leadPageCount })}
                                                </Typography>
                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                    <IconButton
                                                        size="sm"
                                                        color="neutral"
                                                        variant="outlined"
                                                        disabled={leadPage === 0}
                                                        onClick={() => setLeadPage(leadPage - 1)}
                                                        sx={{ bgcolor: 'background.surface' }}
                                                    >
                                                        <KeyboardArrowLeft />
                                                    </IconButton>
                                                    <IconButton
                                                        size="sm"
                                                        color="neutral"
                                                        variant="outlined"
                                                        disabled={leadPage >= Math.ceil(leadPageCount / leadRowsPerPage) - 1}
                                                        onClick={() => setLeadPage(leadPage + 1)}
                                                        sx={{ bgcolor: 'background.surface' }}
                                                    >
                                                        <KeyboardArrowRight />
                                                    </IconButton>
                                                </Box>
                                            </Box>
                                        </td>
                                    </tr>
                                    </tfoot>
                                </Table>
                            </ResponsiveTableWrapper>
                        </TabPanel>

                        <TabPanel value={1} sx={{ px: 0 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
                                {(chainsData?.chains || []).map((chain) => (
                                    <Card key={chain.slug} variant="outlined" sx={{ width: '100%', boxSizing: 'border-box' }}>
                                        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={2}>
                                            <Box>
                                                <Typography level="h4">{chain.name}</Typography>
                                                <Typography level="body-sm" color="neutral">{chain.description}</Typography>
                                                <Chip size="sm" variant="soft" sx={{ mt: 1 }}>{chain.message_stream}</Chip>
                                            </Box>
                                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                                <Button
                                                    variant="outlined"
                                                    onClick={() => openTestEmailModal(chain)}
                                                >
                                                    Send test email
                                                </Button>
                                                <Button
                                                    onClick={() => addAllToChain(chain)}
                                                    loading={actionLoading === chain.slug}
                                                >
                                                    Add all franchise leads
                                                </Button>
                                            </Stack>
                                        </Stack>
                                        <Divider sx={{ my: 2 }} />
                                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' }, gap: 1.5 }}>
                                            <SummaryCard icon={<GroupRoundedIcon />} label="Enrolled" value={chain.counts.total_enrolled} />
                                            <SummaryCard icon={<EmailRoundedIcon />} label="Active" value={chain.counts.active} colour="success" />
                                            <SummaryCard icon={<MarkEmailReadRoundedIcon />} label="Sent" value={chain.counts.sent} />
                                            <SummaryCard icon={<WarningRoundedIcon />} label="Failed" value={chain.counts.failed} colour={chain.counts.failed ? 'danger' : 'neutral'} />
                                        </Box>
                                        <ResponsiveTableWrapper minWidth={650}>
                                            <Table size="sm" sx={{ mt: 2 }}>
                                                <thead>
                                                <tr>
                                                    <th>Email</th>
                                                    <th>Subject</th>
                                                    <th>Send After</th>
                                                    <th>Template</th>
                                                </tr>
                                                </thead>
                                                <tbody>
                                                {chain.emails.map((email) => (
                                                    <tr key={email.stage}>
                                                        <td>#{email.stage}</td>
                                                        <td>{email.subject}</td>
                                                        <td>{email.send_after_days} days</td>
                                                        <td>{email.template}</td>
                                                    </tr>
                                                ))}
                                                </tbody>
                                            </Table>
                                        </ResponsiveTableWrapper>
                                    </Card>
                                ))}
                            </Box>
                        </TabPanel>

                        <TabPanel value={2} sx={{ px: 0 }}>
                            <Sheet variant="outlined" sx={{ borderRadius: 'md', overflow: 'hidden' }}>
                                <ResponsiveTableWrapper minWidth={900}>
                                    <Table stickyHeader hoverRow>
                                        <thead>
                                        <tr>
                                            <th>Event</th>
                                            <th>Lead</th>
                                            <th>Chain</th>
                                            <th>Email</th>
                                            <th>Subject</th>
                                            <th>Details</th>
                                            <th>Created</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {paginatedEvents.map((event) => (
                                            <tr key={event.id}>
                                                <td><Chip size="sm" variant="soft">{event.event_type_display}</Chip></td>
                                                <td>
                                                    <Typography level="title-sm">{event.lead.name}</Typography>
                                                    <Typography level="body-xs" color="neutral">{event.lead.email}</Typography>
                                                </td>
                                                <td>{event.chain_slug || '-'}</td>
                                                <td>{event.email_stage ? `#${event.email_stage}` : '-'}</td>
                                                <td>{event.subject || '-'}</td>
                                                <td>
                                                    <Typography
                                                        level="body-xs"
                                                        color={event.event_type === 'failed' ? 'danger' : 'neutral'}
                                                        sx={{ maxWidth: 360, whiteSpace: 'normal' }}
                                                    >
                                                        {eventDetailsText(event)}
                                                    </Typography>
                                                </td>
                                                <td>{formatDate(event.created_at)}</td>
                                            </tr>
                                        ))}
                                        {!paginatedEvents.length && (
                                            <tr>
                                                <td colSpan={7} style={{ textAlign: 'center', padding: '2rem' }}>
                                                    <Typography color="neutral">No activity found.</Typography>
                                                </td>
                                            </tr>
                                        )}
                                        </tbody>
                                        <tfoot>
                                        <tr>
                                            <td colSpan={7}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'flex-end' }}>
                                                    <Typography textAlign="center" sx={{ minWidth: 90 }}>
                                                        {labelDisplayedRows({ from: eventFrom, to: eventTo, count: eventPageCount })}
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                                        <IconButton
                                                            size="sm"
                                                            color="neutral"
                                                            variant="outlined"
                                                            disabled={eventPage === 0}
                                                            onClick={() => setEventPage(eventPage - 1)}
                                                            sx={{ bgcolor: 'background.surface' }}
                                                        >
                                                            <KeyboardArrowLeft />
                                                        </IconButton>
                                                        <IconButton
                                                            size="sm"
                                                            color="neutral"
                                                            variant="outlined"
                                                            disabled={eventPage >= Math.ceil(eventPageCount / eventRowsPerPage) - 1}
                                                            onClick={() => setEventPage(eventPage + 1)}
                                                            sx={{ bgcolor: 'background.surface' }}
                                                        >
                                                            <KeyboardArrowRight />
                                                        </IconButton>
                                                    </Box>
                                                </Box>
                                            </td>
                                        </tr>
                                        </tfoot>
                                    </Table>
                                </ResponsiveTableWrapper>
                            </Sheet>
                        </TabPanel>
                    </Tabs>

                    <Modal open={Boolean(testEmailChain)} onClose={() => setTestEmailChain(null)}>
                        <ModalDialog>
                            <ModalClose />
                            <Typography level="h4">Send test email</Typography>
                            <Typography level="body-sm" color="neutral">
                                Send a preview from {testEmailChain?.name || 'this chain'}.
                            </Typography>
                            <Stack spacing={1.5} sx={{ mt: 1, minWidth: { xs: 280, sm: 420 } }}>
                                <Box>
                                    <FormLabel>Email address</FormLabel>
                                    <Input
                                        value={testEmailAddress}
                                        onChange={(event) => setTestEmailAddress(event.target.value)}
                                        placeholder="oliver@yeticlean.co.uk"
                                    />
                                </Box>
                                <Box>
                                    <FormLabel>Email in chain</FormLabel>
                                    <Select
                                        value={testEmailStage}
                                        onChange={(_, value) => setTestEmailStage(Number(value || 1))}
                                    >
                                        {(testEmailChain?.emails || []).map((email) => (
                                            <Option key={email.stage} value={email.stage}>
                                                #{email.stage} - {email.subject}
                                            </Option>
                                        ))}
                                    </Select>
                                </Box>
                                {testEmailMessage && (
                                    <Alert color={testEmailMessage.startsWith('Test email sent') ? 'success' : 'danger'}>
                                        {testEmailMessage}
                                    </Alert>
                                )}
                                <Stack direction="row" spacing={1} justifyContent="flex-end">
                                    <Button variant="plain" color="neutral" onClick={() => setTestEmailChain(null)}>
                                        Close
                                    </Button>
                                    <Button
                                        onClick={sendTestEmail}
                                        loading={actionLoading === `test-${testEmailChain?.slug}`}
                                    >
                                        Send test email
                                    </Button>
                                </Stack>
                            </Stack>
                        </ModalDialog>
                    </Modal>
                </>
            )}
        </>
    );
}
